import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import Layouts from "./components/common/Layout";
import Admin from "./pages/Admin";
import AdminValues from "./pages/AdminValues";
import AllSubsActive from "./pages/AllActiveSubs";
import AllNonActiveSubs from "./pages/AllNonActiveSubs";
import Analytics from "./pages/Analytics";
import ExecutiveDashboard from "./pages/analytics/ExecutiveDashboard";
import ContentAnalytics from "./pages/analytics/ContentAnalytics";
import SubscriptionAnalytics from "./pages/analytics/SubscriptionAnalytics";
import PaymentAnalytics from "./pages/analytics/PaymentAnalytics";
import DeviceAnalytics from "./pages/analytics/DeviceAnalytics";
import SearchAnalytics from "./pages/analytics/SearchAnalytics";
import LiveStreamingAnalytics from "./pages/analytics/LiveStreamingAnalytics";
import AdAnalytics from "./pages/analytics/AdAnalytics";
import ErrorSecurityAnalytics from "./pages/analytics/ErrorSecurityAnalytics";
import OperationalAnalytics from "./pages/analytics/OperationalAnalytics";
import UserAnalytics from "./pages/analytics/UserAnalytics";
import AddBanner from "./pages/banner/Add";
import Banners from "./pages/banner/Banners";

import AddCategory from "./pages/categories/Add";
import Categories from "./pages/categories/Categories";
import AddSubCategory from "./pages/categories/subcategory/Add";
import SubCategories from "./pages/categories/subcategory/SubCategories";
import AddComingSoon from "./pages/coming-soon/Add";
import CommingSoon from "./pages/coming-soon/CommingSoon";
import AddCreator from "./pages/creators/Add";
import CreatorDashboard from "./pages/creators/CreatorDashboard";
import CreatorRevenue from "./pages/creators/CreatorRevenue";
import Creators from "./pages/creators/Creators";
import NonVerifiedCreators from "./pages/creators/NonVerified";
import VerifiedCreators from "./pages/creators/Verified";
import Dashboard from "./pages/Dashboard";
import AddDocumentary from "./pages/documentaries/Add";
import Documentaries from "./pages/documentaries/Documentaries";
import Episodes from "./pages/Episodes";
import Genres from "./pages/Genres";
import Languages from "./pages/Languages";
import Login from "./pages/Login";
import AddMovies from "./pages/movies/Add";
import Movies from "./pages/movies/Movies";
import AddMusicVideo from "./pages/music-videos/Add";
import MusicVideos from "./pages/music-videos/MusicVideos";
import Notification from "./pages/Notification";
import Poster from "./pages/Poster";
import Rent from "./pages/Rent";
import AddSeries from "./pages/series/Add";
import AddSeason from "./pages/series/AddSeason";
import Seasons from "./pages/series/Seasons";
import Series from "./pages/series/Series";
import AddShortFilm from "./pages/short-films/Add";
import ShortFilms from "./pages/short-films/ShortFilms";
import AddUser from "./pages/user/Add";
import DisabledUsers from "./pages/user/DisabledUsers";
import EnabledUsers from "./pages/user/EnabledUsers";
import UploadLink from "./pages/UploadLink";
import VideoPlayer from "./pages/VideoPlayer";
import BunnyLibrary from "./pages/BunnyLibrary";
import WhatsAppNotification from "./pages/WhatsAppNotification";
import AddVerticalSeries from "./pages/vertical-series/Add";
import VerticalEpisodes from "./pages/vertical-series/VerticalEpisodes";
import VerticalSeries from "./pages/vertical-series/VerticalSeries";
import AddLiveChannel from "./pages/live-channels/Add";
import LiveChannels from "./pages/live-channels/LiveChannels";
import useStore from "./utils/store";

function App() {
  const isLoggedIn = useStore((state) => state.isLoggedIn);
  const logout = useStore((state) => state.logout);
  const [hydrated, setHydrated] = React.useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Initialize hydration state once on component mount
  React.useEffect(() => {
    setHydrated(true);
  }, []);

  // Handle authentication redirects after hydration
  React.useEffect(() => {
    if (hydrated) {
      if (isLoggedIn) {
        if (pathname === "/login") {
          navigate("/", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    }
  }, [isLoggedIn, hydrated, navigate, pathname]);

  // Add effect to handle token expiration or invalid token
  React.useEffect(() => {
    const handleUnauthorized = (event) => {
      if (event.detail === 401 || event.detail === 403) {
        localStorage.removeItem("tburudani_token");
        logout();
        navigate("/login", { replace: true });
      }
    };

    window.addEventListener("unauthorized", handleUnauthorized);
    return () => {
      window.removeEventListener("unauthorized", handleUnauthorized);
    };
  }, [logout, navigate]);

  return (
    <QueryClientProvider client={new QueryClient()}>
        <Routes>
          <Route
            path="/"
            element={
              <Layouts>
                <Dashboard />
              </Layouts>
            }
          />
          <Route
            path="/analytics"
            element={
              <Layouts>
                <Analytics />
              </Layouts>
            }
          />
          <Route
            path="/analytics/executive"
            element={
              <Layouts>
                <ExecutiveDashboard />
              </Layouts>
            }
          />
          <Route
            path="/analytics/content"
            element={
              <Layouts>
                <ContentAnalytics />
              </Layouts>
            }
          />
          <Route
            path="/analytics/subscriptions"
            element={
              <Layouts>
                <SubscriptionAnalytics />
              </Layouts>
            }
          />
          <Route
            path="/analytics/payments"
            element={
              <Layouts>
                <PaymentAnalytics />
              </Layouts>
            }
          />
          <Route
            path="/analytics/devices"
            element={
              <Layouts>
                <DeviceAnalytics />
              </Layouts>
            }
          />
          <Route
            path="/analytics/search"
            element={
              <Layouts>
                <SearchAnalytics />
              </Layouts>
            }
          />
          <Route
            path="/analytics/live-streaming"
            element={
              <Layouts>
                <LiveStreamingAnalytics />
              </Layouts>
            }
          />
          <Route
            path="/analytics/ads"
            element={
              <Layouts>
                <AdAnalytics />
              </Layouts>
            }
          />
          <Route
            path="/analytics/errors-security"
            element={
              <Layouts>
                <ErrorSecurityAnalytics />
              </Layouts>
            }
          />
          <Route
            path="/analytics/operational"
            element={
              <Layouts>
                <OperationalAnalytics />
              </Layouts>
            }
          />
          <Route
            path="/analytics/users"
            element={
              <Layouts>
                <UserAnalytics />
              </Layouts>
            }
          />
          <Route
            path="/partners/dashboard/:id"
            element={
              <Layouts>
                <CreatorDashboard />
              </Layouts>
            }
          />
          <Route
            path="/partners/revenue/:id"
            element={
              <Layouts>
                <CreatorRevenue />
              </Layouts>
            }
          />
          <Route
            path="/coming-soon"
            element={
              <Layouts>
                <CommingSoon />
              </Layouts>
            }
          />
          <Route
            path="/coming-soon/add"
            element={
              <Layouts>
                <AddComingSoon />
              </Layouts>
            }
          />
          <Route
            path="/coming-soon/edit/:id"
            element={
              <Layouts>
                <AddComingSoon />
              </Layouts>
            }
          />
          <Route
            path="/banners"
            element={
              <Layouts>
                <Banners />
              </Layouts>
            }
          />
          <Route
            path="/banners/add"
            element={
              <Layouts>
                <AddBanner />
              </Layouts>
            }
          />

          <Route
            path="/banners/edit/:id"
            element={
              <Layouts>
                <AddBanner />
              </Layouts>
            }
          />
          <Route
            path="/video"
            element={
              <Layouts>
                <VideoPlayer />
              </Layouts>
            }
          />
          <Route
            path="/bunny"
            element={
              <Layouts>
                <BunnyLibrary />
              </Layouts>
            }
          />
          <Route
            path="/poster"
            element={
              <Layouts>
                <Poster />
              </Layouts>
            }
          />
          <Route
            path="/categories"
            element={
              <Layouts>
                <Categories />
              </Layouts>
            }
          />
          <Route
            path="/categories/add"
            element={
              <Layouts>
                <AddCategory />
              </Layouts>
            }
          />
          <Route
            path="/categories/edit/:id"
            element={
              <Layouts>
                <AddCategory />
              </Layouts>
            }
          />
          <Route
            path="/subcategories/:id/:name"
            element={
              <Layouts>
                <SubCategories />
              </Layouts>
            }
          />
          <Route
            path="/sub/:cat/add"
            element={
              <Layouts>
                <AddSubCategory />
              </Layouts>
            }
          />
          <Route
            path="/sub/:cat/edit/:id"
            element={
              <Layouts>
                <AddSubCategory />
              </Layouts>
            }
          />
          <Route
            path="/genres"
            element={
              <Layouts>
                <Genres />
              </Layouts>
            }
          />
          <Route
            path="/movies"
            element={
              <Layouts>
                <Movies status="Approved" />
              </Layouts>
            }
          />
          <Route
            path="/movies/add"
            element={
              <Layouts>
                <AddMovies />
              </Layouts>
            }
          />
          <Route
            path="/movies/edit/:id"
            element={
              <Layouts>
                <AddMovies />
              </Layouts>
            }
          />
          <Route
            path="/series"
            element={
              <Layouts>
                <Series status="Approved" />
              </Layouts>
            }
          />
          <Route
            path="/series/add"
            element={
              <Layouts>
                <AddSeries status="Approved" />
              </Layouts>
            }
          />
          <Route
            path="/series/pending"
            element={
              <Layouts>
                <Series status="Pending" />
              </Layouts>
            }
          />
          <Route
            path="/series/rejected"
            element={
              <Layouts>
                <Series status="Rejected" />
              </Layouts>
            }
          />
          <Route
            path="/movies"
            element={
              <Layouts>
                <Movies />
              </Layouts>
            }
          />
          <Route
            path="/movies/pending"
            element={
              <Layouts>
                <Movies status="Pending" />
              </Layouts>
            }
          />
          <Route
            path="/movies/rejected"
            element={
              <Layouts>
                <Movies status="Rejected" />
              </Layouts>
            }
          />
          <Route
            path="/documentaries"
            element={
              <Layouts>
                <Documentaries status="Approved" />
              </Layouts>
            }
          />
          <Route
            path="/documentaries/add"
            element={
              <Layouts>
                <AddDocumentary />
              </Layouts>
            }
          />
          <Route
            path="/documentaries/edit/:id"
            element={
              <Layouts>
                <AddDocumentary />
              </Layouts>
            }
          />
          <Route
            path="/documentaries/pending"
            element={
              <Layouts>
                <Documentaries status="Pending" />
              </Layouts>
            }
          />
          <Route
            path="/documentaries/rejected"
            element={
              <Layouts>
                <Documentaries status="Rejected" />
              </Layouts>
            }
          />
          <Route
            path="/music-videos"
            element={
              <Layouts>
                <MusicVideos status="Approved" />
              </Layouts>
            }
          />
          <Route
            path="/music-videos/add"
            element={
              <Layouts>
                <AddMusicVideo />
              </Layouts>
            }
          />
          <Route
            path="/music-videos/edit/:id"
            element={
              <Layouts>
                <AddMusicVideo />
              </Layouts>
            }
          />
          <Route
            path="/music-videos/pending"
            element={
              <Layouts>
                <MusicVideos status="Pending" />
              </Layouts>
            }
          />
          <Route
            path="/music-videos/rejected"
            element={
              <Layouts>
                <MusicVideos status="Rejected" />
              </Layouts>
            }
          />
          <Route
            path="/short-films"
            element={
              <Layouts>
                <ShortFilms status="Approved" />
              </Layouts>
            }
          />
          <Route
            path="/short-films/add"
            element={
              <Layouts>
                <AddShortFilm />
              </Layouts>
            }
          />
          <Route
            path="/short-films/edit/:id"
            element={
              <Layouts>
                <AddShortFilm />
              </Layouts>
            }
          />
          <Route
            path="/short-films/pending"
            element={
              <Layouts>
                <ShortFilms status="Pending" />
              </Layouts>
            }
          />
          <Route
            path="/short-films/rejected"
            element={
              <Layouts>
                <ShortFilms status="Rejected" />
              </Layouts>
            }
          />
          <Route
            path="/series"
            element={
              <Layouts>
                <Series status="Approved" />
              </Layouts>
            }
          />
          <Route
            path="/series/add"
            element={
              <Layouts>
                <AddSeries status="Approved" />
              </Layouts>
            }
          />
          <Route
            path="/series/pending"
            element={
              <Layouts>
                <Series status="Pending" />
              </Layouts>
            }
          />
          <Route
            path="/series/rejected"
            element={
              <Layouts>
                <Series status="Rejected" />
              </Layouts>
            }
          />
          <Route
            path="/language"
            element={
              <Layouts>
                <Languages />
              </Layouts>
            }
          />
          <Route
            path="/partners"
            element={
              <Layouts>
                <Creators />
              </Layouts>
            }
          />
          <Route
            path="/partners/verified"
            element={
              <Layouts>
                <VerifiedCreators />
              </Layouts>
            }
          />
          <Route
            path="/partners/non-verified"
            element={
              <Layouts>
                <NonVerifiedCreators />
              </Layouts>
            }
          />
          <Route
            path="/partners/add"
            element={
              <Layouts>
                <AddCreator />
              </Layouts>
            }
          />
          <Route
            path="/partners/edit/:id"
            element={
              <Layouts>
                <AddCreator />
              </Layouts>
            }
          />
          <Route
            path="/admin"
            element={
              <Layouts>
                <Admin />
              </Layouts>
            }
          />
          <Route
            path="/series/edit/:id"
            element={
              <Layouts>
                <AddSeries />
              </Layouts>
            }
          />

          <Route
            path="/seasons/:seriesId"
            element={
              <Layouts>
                <Seasons />
              </Layouts>
            }
          />

          <Route
            path="/seasons/add/:seriesId"
            element={
              <Layouts>
                <AddSeason />
              </Layouts>
            }
          />

          <Route
            path="/seasons/edit/:seriesId/:id"
            element={
              <Layouts>
                <AddSeason />
              </Layouts>
            }
          />

          <Route
            path="/episodes/:seasonId"
            element={
              <Layouts>
                <Episodes />
              </Layouts>
            }
          />
          <Route
            path="/episodes/edit/:id"
            element={
              <Layouts>
                <Episodes />
              </Layouts>
            }
          />
          <Route
            path="/users"
            element={
              <Layouts>
                <EnabledUsers />
              </Layouts>
            }
          />
          <Route
            path="/users/disabled"
            element={
              <Layouts>
                <DisabledUsers />
              </Layouts>
            }
          />
          <Route
            path="/users/add"
            element={
              <Layouts>
                <AddUser />
              </Layouts>
            }
          />
          <Route
            path="/users/edit/:id"
            element={
              <Layouts>
                <AddUser />
              </Layouts>
            }
          />
          <Route
            path="/notification"
            element={
              <Layouts>
                <Notification />
              </Layouts>
            }
          />
          <Route
            path="/rent"
            element={
              <Layouts>
                <Rent />
              </Layouts>
            }
          />
          <Route
            path="/admin-values"
            element={
              <Layouts>
                <AdminValues />
              </Layouts>
            }
          />
          <Route
            path="/active-subscription"
            element={
              <Layouts>
                <AllSubsActive />
              </Layouts>
            }
          />
          <Route
            path="/admin/non-active-subscriptions"
            element={
              <Layouts>
                <AllNonActiveSubs />
              </Layouts>
            }
          />
          <Route
            path="/whatsapp-notification"
            element={
              <Layouts>
                <WhatsAppNotification />
              </Layouts>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route
            path="/analytics/:type/:id"
            element={
              <Layouts>
                <Analytics />
              </Layouts>
            }
          />
          <Route
            path="/upload-link"
            element={
              <Layouts>
                <UploadLink />
              </Layouts>
            }
          />
          <Route
            path="/vertical-series"
            element={
              <Layouts>
                <VerticalSeries />
              </Layouts>
            }
          />
          <Route
            path="/vertical-series/add"
            element={
              <Layouts>
                <AddVerticalSeries />
              </Layouts>
            }
          />
          <Route
            path="/vertical-series/edit/:id"
            element={
              <Layouts>
                <AddVerticalSeries />
              </Layouts>
            }
          />
          <Route
            path="/vertical-episodes/:id"
            element={
              <Layouts>
                <VerticalEpisodes />
              </Layouts>
            }
          />
          <Route
            path="/live-channels"
            element={
              <Layouts>
                <LiveChannels />
              </Layouts>
            }
          />
          <Route
            path="/live-channels/add"
            element={
              <Layouts>
                <AddLiveChannel />
              </Layouts>
            }
          />
          <Route
            path="/live-channels/edit/:id"
            element={
              <Layouts>
                <AddLiveChannel />
              </Layouts>
            }
          />
        </Routes>
    </QueryClientProvider>
  );
}

export default App;
